import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield } from "lucide-react";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";
import { m } from "@/paraglide/messages";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Toolverse" },
      {
        name: "description",
        content:
          "Toolverse privacy policy: what data we collect, how we use it, and your rights as a user.",
      },
      { property: "og:title", content: "Privacy Policy — Toolverse" },
      {
        property: "og:description",
        content:
          "Toolverse privacy policy: what data we collect, how we use it, and your rights.",
      },
      { name: "robots", content: "index, follow" },
      ...i18nOgMeta("/privacy"),
    ],
    links: [...i18nLinks("/privacy")],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="flex-1 bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Shield className="h-3 w-3 text-primary" />
            <span className="text-[11px] font-semibold text-primary tracking-wide uppercase">
              {m.privacy_badge()}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
            {m.privacy_page_title()}
          </h1>
          <p className="text-sm text-muted-foreground">
            {m.privacy_last_updated()}
          </p>
        </div>

        <div className="space-y-10 text-sm md:text-base text-muted-foreground leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_1_title()}
            </h2>
            <p>{m.privacy_section_1_text()}</p>
            <p className="mt-3">{m.privacy_section_1_applies()}</p>
            <p className="mt-3">{m.privacy_not_affiliated()}</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_2_title()}
            </h2>
            <p className="mb-3">{m.privacy_section_2_intro()}</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>{m.privacy_info_account()}</li>
              <li>{m.privacy_info_payment()}</li>
              <li>{m.privacy_info_usage()}</li>
              <li>{m.privacy_info_device()}</li>
              <li>{m.privacy_info_preferences()}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_3_title()}
            </h2>
            <p className="mb-3">{m.privacy_section_3_intro()}</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>{m.privacy_use_1()}</li>
              <li>{m.privacy_use_2()}</li>
              <li>{m.privacy_use_3()}</li>
              <li>{m.privacy_use_4()}</li>
              <li>{m.privacy_use_5()}</li>
              <li>{m.privacy_use_6()}</li>
              <li>{m.privacy_use_7()}</li>
              <li>{m.privacy_use_8()}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_4_title()}
            </h2>
            <p className="mb-3">{m.privacy_section_4_intro()}</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>{m.privacy_cookie_essential()}</li>
              <li>{m.privacy_cookie_analytics()}</li>
              <li>{m.privacy_cookie_advertising()}</li>
            </ul>
            <p className="mt-3">{m.privacy_cookie_manage()}</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_5_title()}
            </h2>
            <p className="mb-3">{m.privacy_section_5_intro()}</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>{m.privacy_third_party_auth()}</li>
              <li>{m.privacy_third_party_payment()}</li>
              <li>{m.privacy_third_party_ads()}</li>
              <li>{m.privacy_third_party_analytics()}</li>
              <li>{m.privacy_third_party_infra()}</li>
            </ul>
            <p className="mt-3">{m.privacy_no_data_sale()}</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_6_title()}
            </h2>
            <p>{m.privacy_section_6_text()}</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_7_title()}
            </h2>
            <p>{m.privacy_section_7_text()}</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_8_title()}
            </h2>
            <p className="mb-3">{m.privacy_section_8_intro()}</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>{m.privacy_right_1()}</li>
              <li>{m.privacy_right_2()}</li>
              <li>{m.privacy_right_3()}</li>
              <li>{m.privacy_right_4()}</li>
              <li>{m.privacy_right_5()}</li>
              <li>{m.privacy_right_6()}</li>
            </ul>
            <p className="mt-3">
              {m.privacy_exercise_rights({ email: "contact@toolverse.me" })}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_9_title()}
            </h2>
            <p>{m.privacy_section_9_text()}</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              {m.privacy_section_10_title()}
            </h2>
            <p>{m.privacy_section_10_text()}</p>
          </section>
        </div>

        <div className="pt-8 border-t border-border mt-10">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {m.privacy_back()}
          </Link>
        </div>
      </div>
    </div>
  );
}
